import React from "react";
import Select from "react-select";
import { capitalizeFirstLetter } from "../../../utils/commonfunction";
import { PALETTE_COLUMNS, getColorDotStyle } from "../../admin/common/colorPalettePicker";

const expandTabValues = (values) => {
  const expanded = [];
  (values || []).forEach((val) => {
    if (val && typeof val === "object" && val.colorCode && val.colorCode.startsWith("tab:")) {
      const tabName = val.colorCode.replace("tab:", "");
      const col = (PALETTE_COLUMNS || []).find((c) => c.name === tabName);
      if (col && col.colors) {
        col.colors.forEach((c) => {
          expanded.push({
            name: c.name,
            colorCode: c.hex,
          });
        });
      }
    } else {
      expanded.push(val);
    }
  });
  return expanded;
};

export default function ProductAttributesSection({
  attributes = [],
  values = [],
  onChange,
  attributeFacets = {},
  colorPalettes = [],
  isLoading = false,
}) {
  if (isLoading) {
    return (
      <div className="mb-4 p-4 bg-zinc-50 rounded-xl border border-zinc-100 flex items-center justify-center text-xs text-gray-500">
        <span className="inline-block animate-spin mr-2">⏳</span> Loading attributes...
      </div>
    );
  }

  if (!attributes || attributes.length === 0) {
    return null;
  }

  const handleValueChange = (attrName, val) => {
    const currentAttrs = Array.isArray(values) ? [...values] : [];
    const index = currentAttrs.findIndex((a) => a && a.name === attrName);
    if (index > -1) {
      currentAttrs[index] = { ...currentAttrs[index], value: val };
    } else {
      currentAttrs.push({ name: attrName, value: val });
    }
    if (onChange) {
      onChange(currentAttrs);
    }
  };

  return (
    <div className="mb-4 p-4 bg-zinc-50 rounded-xl border border-zinc-100">
      <label className="block font-bold text-zinc-800 mb-4 text-sm">Attributes</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {attributes.map((attr) => {
          if (!attr || !attr.name) return null;

          const attrEntry = Array.isArray(values) ? values.find((a) => a && a.name === attr.name) : null;
          const currentValue = attrEntry ? attrEntry.value : "";

          const facetValues = attributeFacets[attr.name] || [];
          const hasPredefinedValues = attr.values && attr.values.length > 0;
          const hasFacetValues = facetValues.length > 0;
          const isDropdownType = ["select", "checkbox", "radio"].includes(attr.attributeType);
          const isUserTextInput = ["text", "number", "user_input"].includes(attr.attributeType) && !hasPredefinedValues;

          const colorMap = {};
          const expandedValues = expandTabValues(attr.values || []);
          const expandedFacetValues = expandTabValues(facetValues || []);

          const attrValuesNames = expandedValues
            .map((val) => {
              if (val && typeof val === "object") {
                if (val.name && typeof val.name === "object" && val.name.name) {
                  const colorName = val.name.name;
                  const colorCode = val.name.colorCode || null;
                  if (colorName) {
                    colorMap[colorName] = colorCode;
                    return colorName;
                  }
                }
                if (val.name && typeof val.name === "string") {
                  const colorName = val.name;
                  const colorCode = val.colorCode || null;
                  colorMap[colorName] = colorCode;
                  return colorName;
                }
              }
              return String(val || "");
            })
            .filter((v) => v.trim() !== "");

          const sanitizedFacetValues = expandedFacetValues
            .map((val) => {
              if (val && typeof val === "object") {
                if (val.name && typeof val.name === "object" && val.name.name) {
                  const colorName = val.name.name;
                  const colorCode = val.name.colorCode || null;
                  if (colorName) {
                    colorMap[colorName] = colorCode;
                    return colorName;
                  }
                }
                if (val.name && typeof val.name === "string") {
                  const colorName = val.name;
                  const colorCode = val.colorCode || null;
                  colorMap[colorName] = colorCode;
                  return colorName;
                }
              }
              return String(val || "");
            })
            .filter((v) => v.trim() !== "");

          const isColorAttr =
            attr.hasColorCode ||
            attr.name.toLowerCase().includes("color") ||
            attr.name.toLowerCase().includes("colour");

          const selectOptions = [
            ...new Set([...attrValuesNames, ...sanitizedFacetValues]),
          ].map((v) => ({
            label: v,
            value: v,
            colorCode: colorMap[v] || null,
          }));

          const isMulti = attr.isMultiSelect || attr.attributeType === "checkbox";

          return (
            <div key={attr._id || attr.name} className="form-group mb-0">
              <label className="text-xs font-semibold text-zinc-600 mb-1 block">
                {capitalizeFirstLetter(attr.name)}
                {isMulti && <span className="text-[10px] ml-1.5 text-zinc-400 font-normal">(Multi-select)</span>}
              </label>

              {isMulti ? (
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                    control: (base) => ({
                      ...base,
                      borderRadius: "0.75rem",
                      borderColor: "#e5e7eb",
                      minHeight: "42px",
                      fontSize: "0.875rem",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#d1d5db" },
                    }),
                  }}
                  className="react-select-container text-sm"
                  classNamePrefix="react-select"
                  options={selectOptions}
                  placeholder={`Select ${attr.name}`}
                  value={
                    Array.isArray(currentValue)
                      ? currentValue.map((v) => ({ label: v, value: v }))
                      : currentValue
                      ? [{ label: currentValue, value: currentValue }]
                      : []
                  }
                  onChange={(selectedOpts) => {
                    const vals = selectedOpts ? selectedOpts.map((o) => o.value) : [];
                    handleValueChange(attr.name, vals);
                  }}
                  formatOptionLabel={({ label, value, colorCode: optColorCode }) => {
                    if (isColorAttr) {
                      const dotStyle = getColorDotStyle(optColorCode || value, colorPalettes);
                      return (
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full inline-block" style={dotStyle} />
                          <span>{label}</span>
                        </div>
                      );
                    }
                    return label;
                  }}
                />
              ) : !isUserTextInput && (hasPredefinedValues || hasFacetValues || isDropdownType) ? (
                <Select
                  className="react-select-container text-sm"
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                    control: (base) => ({
                      ...base,
                      borderRadius: "0.75rem",
                      borderColor: "#e5e7eb",
                      minHeight: "42px",
                      fontSize: "0.875rem",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#d1d5db" },
                    }),
                  }}
                  options={selectOptions}
                  placeholder={`Select ${attr.name}`}
                  value={
                    selectOptions.find((o) => o.value === currentValue) ||
                    (currentValue ? { label: currentValue, value: currentValue } : null)
                  }
                  onChange={(selectedOpt) => {
                    handleValueChange(attr.name, selectedOpt ? selectedOpt.value : "");
                  }}
                  isClearable
                  formatOptionLabel={({ label, value, colorCode: optColorCode }) => {
                    if (isColorAttr) {
                      const finalColorCode =
                        optColorCode ||
                        (String(value || "").toLowerCase() === "nude"
                          ? "#E3BC9A"
                          : String(value || "").toLowerCase() === "transparent"
                          ? "transparent"
                          : String(value || "").toLowerCase().replace(/ /g, ""));
                      return (
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full border border-gray-300 inline-block"
                            style={{
                              backgroundColor: finalColorCode,
                              backgroundImage:
                                String(value || "").toLowerCase() === "transparent"
                                  ? "repeating-conic-gradient(#ccc 0% 25%, white 0% 50%) 50% / 100% 100%"
                                  : "none",
                            }}
                          />
                          <span>{label}</span>
                        </div>
                      );
                    }
                    return label;
                  }}
                />
              ) : (
                <input
                  type={attr.attributeType === "number" ? "number" : "text"}
                  className="form-control-custom w-full text-sm"
                  placeholder={`Enter ${attr.name}`}
                  value={currentValue || ""}
                  min={attr.attributeType === "number" ? "0" : undefined}
                  onKeyDown={(e) => {
                    if (attr.attributeType === "number" && (e.key === "-" || e.key === "Minus")) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (attr.attributeType === "number" && val !== "") {
                      if (val.includes("-") || parseFloat(val) < 0) {
                        return;
                      }
                    }
                    handleValueChange(attr.name, val);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
